import axios from 'axios';
import { getAppCheckToken } from '../firebase';

const API_BASE_URL = import.meta.env?.VITE_API_URL || 
  (import.meta.env.PROD 
    ? 'https://api.blip-platform.com/api' 
    : 'http://localhost:5000/api');

console.log('🔗 API Base URL:', API_BASE_URL);
console.log('🌍 Environment - PROD:', import.meta.env.PROD);
console.log('🌍 Environment - VITE_API_URL:', import.meta.env?.VITE_API_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request interceptor to add auth token + Firebase App Check token
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const appCheckToken = await getAppCheckToken();
    if (appCheckToken) {
      config.headers['X-Firebase-AppCheck'] = appCheckToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API methods
export const authAPI = {
  register: async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    displayName?: string;
    bio?: string;
    phone?: string;
    socialMediaLinks?: Array<{ platform: string; url: string }>;
  }) => {
    console.log('📝 Attempting registration with:', { ...userData, password: '[HIDDEN]', baseURL: API_BASE_URL });
    try {
      const response = await api.post('/auth/register', userData);
      console.log('✅ Registration successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Registration failed:', error);
      throw error;
    }
  },

  login: async (email: string, password: string) => {
    console.log('🔐 Attempting login with:', { email, baseURL: API_BASE_URL });
    try {
      const response = await api.post('/auth/login', { email, password });
      console.log('✅ Login successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  googleLogin: async (token: string) => {
    console.log('🔐 Attempting Google login with token');
    try {
      const response = await api.post('/auth/google', { token });
      console.log('✅ Google login successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Google login failed:', error);
      throw error;
    }
  },

  metaLogin: async (accessToken: string) => {
    console.log('🔐 Attempting Meta login with access token');
    try {
      const response = await api.post('/auth/meta', { accessToken });
      console.log('✅ Meta login successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Meta login failed:', error);
      throw error;
    }
  }
};

// Tours API methods
export const toursAPI = {
  getAllTours: async (filters?: {
    city?: string;
    difficulty?: string;
    maxPrice?: number;
    search?: string;
    includeGuide?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (filters?.city) params.append('city', filters.city);
    if (filters?.difficulty) params.append('difficulty', filters.difficulty);
    if (filters?.maxPrice) params.append('maxPrice', filters.maxPrice.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.includeGuide) params.append('includeGuide', 'true');
    
    const response = await api.get(`/tours?${params.toString()}`);
    return response.data;
  },

  getTourById: async (id: string) => {
    const response = await api.get(`/tours/${id}`);
    return response.data;
  },

  createTour: async (tourData: any) => {
    const response = await api.post('/tours', tourData);
    return response.data;
  },

  updateTour: async (id: string, tourData: any) => {
    const response = await api.put(`/tours/${id}`, tourData);
    return response.data;
  },

  updateTourStatus: async (id: string, status: 'draft' | 'active' | 'inactive' | 'archived') => {
    const response = await api.patch(`/tours/${id}/status`, { status });
    return response.data;
  },

  deleteTour: async (id: string) => {
    const response = await api.delete(`/tours/${id}`);
    return response.data;
  },

  getMyTours: async () => {
    const response = await api.get('/tours/my-tours');
    return response.data;
  }
};

// Export individual functions for convenience
export const createTour = toursAPI.createTour;
export const getAllTours = toursAPI.getAllTours;
export const getTourById = toursAPI.getTourById;
export const updateTour = toursAPI.updateTour;
export const updateTourStatus = toursAPI.updateTourStatus;
export const getMyTours = toursAPI.getMyTours;

// Bookings API methods
export const bookingsAPI = {
  createBooking: async (bookingData: {
    tour_id: number;
    booking_date: string;
    participants: number;
    special_requests?: string;
    validity_days?: number;
  }) => {
    const response = await api.post('/bookings', bookingData);
    return response.data;
  },

  getUserBookings: async () => {
    const response = await api.get('/bookings');
    return response.data;
  },

  getBookingById: async (id: string) => {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  },

  startTour: async (bookingId: string) => {
    const response = await api.put(`/bookings/${bookingId}/start`);
    return response.data;
  },

  updateProgress: async (bookingId: string, waypoint_id: number, completed: boolean) => {
    const response = await api.put(`/bookings/${bookingId}/progress`, {
      waypoint_id,
      completed
    });
    return response.data;
  },

  completeTour: async (bookingId: string) => {
    const response = await api.put(`/bookings/${bookingId}/complete`);
    return response.data;
  },

  resetTour: async (bookingId: string) => {
    const response = await api.put(`/bookings/${bookingId}/reset`);
    return response.data;
  },

  updateBookingStatus: async (bookingId: string, status: string) => {
    const response = await api.put(`/bookings/${bookingId}/status`, { status });
    return response.data;
  }
};

// Export booking functions for convenience
export const createBooking = bookingsAPI.createBooking;
export const getUserBookings = bookingsAPI.getUserBookings;
export const getBookingById = bookingsAPI.getBookingById;
export const startTour = bookingsAPI.startTour;
export const updateProgress = bookingsAPI.updateProgress;
export const completeTour = bookingsAPI.completeTour;
export const resetTour = bookingsAPI.resetTour;

// Users API methods
export const usersAPI = {
  getUserById: async (userId: string) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  getUserProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  updateUserProfile: async (userData: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    bio?: string;
    phone?: string;
    iconUrl?: string;
    socialMediaLinks?: Array<{ platform: string; url: string }>;
  }) => {
    const response = await api.put('/users/profile', userData);
    return response.data;
  },

  updatePassword: async (passwordData: {
    currentPassword: string;
    newPassword: string;
  }) => {
    const response = await api.put('/users/password', passwordData);
    return response.data;
  },

  deleteAccount: async () => {
    const response = await api.delete('/users/account');
    return response.data;
  },

  exportUserData: async () => {
    const response = await api.get('/users/export-data');
    return response.data;
  }
};

// Export user functions for convenience
export const getUserById = usersAPI.getUserById;
export const getUserProfile = usersAPI.getUserProfile;
export const updateUserProfile = usersAPI.updateUserProfile;
export const updatePassword = usersAPI.updatePassword;
export const deleteAccount = usersAPI.deleteAccount;
export const exportUserData = usersAPI.exportUserData;

// Guides API methods
export const guidesAPI = {
  getGuideById: async (id: string) => {
    const response = await api.get(`/guides/${id}`);
    return response.data;
  },

  getGuideByUserId: async (userId: string) => {
    const response = await api.get(`/guides/user/${userId}`);
    return response.data;
  },

  createGuideProfile: async (guideData: {
    display_name: string;
    bio?: string;
    profile_photo_url?: string;
  }) => {
    const response = await api.post('/guides', guideData);
    return response.data;
  },

  updateGuideProfile: async (id: string, guideData: {
    display_name?: string;
    bio?: string;
    profile_photo_url?: string;
  }) => {
    const response = await api.put(`/guides/${id}`, guideData);
    return response.data;
  },

  deleteGuideProfile: async (id: string) => {
    const response = await api.delete(`/guides/${id}`);
    return response.data;
  }
};

// Export guide functions for convenience
export const getGuideById = guidesAPI.getGuideById;
export const getGuideByUserId = guidesAPI.getGuideByUserId;
export const createGuideProfile = guidesAPI.createGuideProfile;
export const updateGuideProfile = guidesAPI.updateGuideProfile;
export const deleteGuideProfile = guidesAPI.deleteGuideProfile;

// Languages API methods
export const languagesAPI = {
  getActiveLanguages: async () => {
    const response = await api.get('/languages?is_active=true');
    return response.data;
  },

  getAllLanguages: async () => {
    const response = await api.get('/languages');
    return response.data;
  },

  getLanguageById: async (id: number) => {
    const response = await api.get(`/languages/${id}`);
    return response.data;
  }
};

// Export language functions for convenience
export const getActiveLanguages = languagesAPI.getActiveLanguages;
export const getAllLanguages = languagesAPI.getAllLanguages;
export const getLanguageById = languagesAPI.getLanguageById;

// Admin API methods
export const adminAPI = {
  // User management
  getPendingUsers: async () => {
    const response = await api.get('/admin/users/pending');
    return response.data;
  },

  approveUser: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/reject`);
    return response.data;
  },

  // POI management
  getPendingPois: async () => {
    const response = await api.get('/admin/pois/pending');
    return response.data;
  },

  approvePoi: async (poiId: number) => {
    const response = await api.post(`/admin/pois/${poiId}/approve`);
    return response.data;
  },

  rejectPoi: async (poiId: number, reason: string) => {
    const response = await api.post(`/admin/pois/${poiId}/reject`, { reason });
    return response.data;
  },

  deletePoi: async (poiIdV2: string) => {
    const response = await api.delete(`/pois/${poiIdV2}`);
    return response.data;
  },

  // Card management
  getPendingCards: async () => {
    const response = await api.get('/admin/cards/pending');
    return response.data;
  },

  approveCard: async (cardId: number) => {
    const response = await api.post(`/admin/cards/${cardId}/approve`);
    return response.data;
  },

  rejectCard: async (cardId: number) => {
    const response = await api.post(`/admin/cards/${cardId}/reject`);
    return response.data;
  },

  // Analytics
  getDAU: async (date?: string) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/admin/analytics/dau${params}`);
    return response.data;
  },

  getDAURange: async (startDate: string, endDate?: string) => {
    const params = new URLSearchParams({ start_date: startDate });
    if (endDate) params.append('end_date', endDate);
    const response = await api.get(`/admin/analytics/dau/range?${params.toString()}`);
    return response.data;
  },

  getDAUUnique: async (dates: string[]) => {
    const response = await api.get(`/admin/analytics/dau/unique?dates=${dates.join(',')}`);
    return response.data;
  }
};

// Export admin functions for convenience
export const getPendingUsers = adminAPI.getPendingUsers;
export const approveUser = adminAPI.approveUser;
export const rejectUser = adminAPI.rejectUser;
export const getPendingPois = adminAPI.getPendingPois;
export const approvePoi = adminAPI.approvePoi;
export const rejectPoi = adminAPI.rejectPoi;
export const deletePoi = adminAPI.deletePoi;
export const getPendingCards = adminAPI.getPendingCards;
export const approveCard = adminAPI.approveCard;
export const rejectCard = adminAPI.rejectCard;
export const getDAU = adminAPI.getDAU;
export const getDAURange = adminAPI.getDAURange;
export const getDAUUnique = adminAPI.getDAUUnique;

export default api;
